import React, { useMemo, useState, useCallback, useEffect } from "react";
import { get } from "lodash";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router";

import {
  getFromUserOrAuth,
  getApiUrlFromAuth0,
  isInternalSupportUser,
  auth0UserMetadataKey,
} from "src/services/auth";
import { syncTenantToUrl } from "src/services/tenantUrl";
import {
  getActiveTenant,
  saveActiveTenant,
  getDateSettingsFromLocalStorage,
  saveDateSettingsToLocalStorage,
  deleteDateSettingsFromLocalStorage,
} from "src/services/localStorage";

import { workspaceIdFromUrl } from "src/consts/urlPaths";
import eventEmitter, { EVENTS } from "src/services/eventEmitter";
import type { DateSettings, OverrideTenant } from "../../../app/apolloClient";
import { Provider } from "./ApolloSettingsContext";

/** Shape of the Auth0 user object as consumed by this component. */
export interface Auth0User {
  sub?: string;
  email?: string;
  name?: string;
  [key: string]: unknown;
}

interface Props {
  children: React.ReactNode;
  user: Auth0User | null | undefined;
  getTokenSilently: () => Promise<string>;
  loginWithRedirect?: (opts: {
    appState: { targetUrl: string };
  }) => Promise<void>;
}

const ApolloSettingsContextProvider = ({
  children,
  user,
  getTokenSilently,
  loginWithRedirect,
}: Props) => {
  const urlWorkspaceId = workspaceIdFromUrl() as string | undefined;
  const history = useHistory();
  const location = useLocation();
  const userSub = get(user, "sub", "") as string;
  const username = get(user, "name") as string | undefined;
  const [overrideTenant, setOverrideTenant] = useState<
    OverrideTenant | undefined
  >(() => getActiveTenant(userSub) as OverrideTenant | undefined);
  // auth.js is untyped JS with its own JSDoc Auth0User typedef; cast at the
  // service boundary rather than in every consumer. Memoised so effects that
  // depend on it re-run only when user changes, not on every render.
  const authUser = useMemo(
    () => (user ?? {}) as Parameters<typeof getFromUserOrAuth>[0],
    [user]
  );

  const [tenantId, setTenantId] = useState<string>(
    get(
      overrideTenant,
      "tenantId",
      getFromUserOrAuth(authUser, "tenantId", undefined)
    ) as string
  );

  const urlFromAuth = getFromUserOrAuth(authUser, "apiUrl", undefined);
  const apiUrl = getApiUrlFromAuth0(
    get(overrideTenant, "tenantId", urlFromAuth),
    tenantId,
    overrideTenant
  ) as string;

  const [workspaceId, _setWorkspaceId] = useState<string | undefined>(
    urlWorkspaceId
  );
  const [workspaceChanged, _setWorkspaceChanged] = useState(false);

  // Sync workspace state with URL on pathname changes.
  // Prevents stale workspace IDs (e.g. "fjak" from /fjak/people/12) persisting when
  // navigating to a URL with no workspace segment (e.g. /user/allocations),
  // which would cause WorkspaceContextProvider to throw WORKSPACE_NOT_FOUND and loop
  // in React 18 concurrent mode via Apollo v4's useSyncExternalStore.
  useEffect(() => {
    _setWorkspaceId(workspaceIdFromUrl() as string | undefined);
  }, [location.pathname]);

  const savedDateSettings = useMemo(
    () =>
      getDateSettingsFromLocalStorage(
        tenantId,
        workspaceId,
        username
      ) as DateSettings,
    [tenantId, workspaceId, username]
  );
  const [dateSettings, _setDateSettings] =
    useState<DateSettings>(savedDateSettings);

  const setWorkspaceId = useCallback(
    (newWorkspaceId: string | undefined, isActive?: boolean) => {
      if (!isActive && !urlWorkspaceId) {
        history.push(
          newWorkspaceId
            ? `/${newWorkspaceId}${location?.pathname}`
            : location?.pathname
        );
      } else if (
        !isActive &&
        urlWorkspaceId &&
        urlWorkspaceId !== newWorkspaceId
      ) {
        history.push(
          newWorkspaceId
            ? location?.pathname.replace(urlWorkspaceId, newWorkspaceId)
            : location?.pathname.replace(`/${urlWorkspaceId}`, "")
        );
      } else if (isActive) {
        history.push(location?.pathname.replace(`/${urlWorkspaceId}`, ""));
      }

      _setWorkspaceId(newWorkspaceId);
      _setWorkspaceChanged(true);
    },
    [location, urlWorkspaceId, history]
  );

  const setWorkspaceChanged = useCallback((value: boolean) => {
    _setWorkspaceChanged(value);
  }, []);

  const setDateSettings = useCallback(
    (changes: Partial<DateSettings>) => {
      const updatedDateSettings: DateSettings = { ...dateSettings, ...changes };
      saveDateSettingsToLocalStorage(
        tenantId,
        workspaceId,
        username,
        updatedDateSettings
      );
      _setDateSettings(updatedDateSettings);
    },
    [dateSettings, tenantId, workspaceId, username]
  );

  const clearDateSettings = useCallback(() => {
    deleteDateSettingsFromLocalStorage(tenantId, workspaceId, username);
    _setDateSettings({});
  }, [tenantId, workspaceId, username]);

  // Hard refresh clears Apollo cache but not localStorage — clear saved dates so stale
  // comparisons don't persist across a Cmd+Shift+R reload.
  useEffect(() => {
    const navigationEntry = performance.getEntriesByType?.(
      "navigation"
    )?.[0] as PerformanceNavigationTiming | undefined;
    const isReload =
      navigationEntry?.type === "reload" || performance.navigation?.type === 1;
    if (isReload) {
      clearDateSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When email impersonation is active, force readonly and persist. Do not gate on
  // isInternalSupportUser(user): after getImpersonatedUser, user.email is the impersonated address.
  useEffect(() => {
    if (
      overrideTenant &&
      overrideTenant.canRunMutation !== false &&
      get(user, [auth0UserMetadataKey, "impersonation", "email"])
    ) {
      const updated: OverrideTenant = {
        ...overrideTenant,
        canRunMutation: false,
      };
      setOverrideTenant(updated);
      saveActiveTenant(userSub, updated);
      eventEmitter.emit(EVENTS.ACTIVE_TENANT_UPDATED, updated);
    }
  }, [overrideTenant, user, userSub]);

  // Sync tenant to URL for internal support users (enables shareable links)
  useEffect(() => {
    if (isInternalSupportUser(authUser) && tenantId) {
      syncTenantToUrl(tenantId, true);
    }
  }, [authUser, tenantId, location.pathname]);

  const values = useMemo(
    () => ({
      overrideTenant,
      tenantId,
      setTenantId,
      workspaceId: urlWorkspaceId || workspaceId,
      setWorkspaceId,
      dateSettings,
      setDateSettings,
      clearDateSettings,
      getTokenSilently,
      ...(loginWithRedirect !== undefined && { loginWithRedirect }),
      workspaceChanged,
      setWorkspaceChanged,
      apiUrl,
    }),
    [
      overrideTenant,
      tenantId,
      setTenantId,
      urlWorkspaceId,
      workspaceId,
      setWorkspaceId,
      dateSettings,
      setDateSettings,
      clearDateSettings,
      getTokenSilently,
      loginWithRedirect,
      workspaceChanged,
      setWorkspaceChanged,
      apiUrl,
    ]
  );

  return <Provider {...values}>{children}</Provider>;
};

export default ApolloSettingsContextProvider;
